import { useCallback, useMemo, useState } from "react";
import type { GordianSeat } from "@b2bportal/air-seats-api";
import type { HopperSeatTotal } from "@checkout/states/Products/Flight/states/SeatSelection/context";
import { useI18nContext } from "@hopper-b2b/i18n";
import type { GordianSeatSegment } from "@hopper-b2b/types";
import { B2BLoadingPopup, BackButton } from "@hopper-b2b/ui";
import type { SeatMapOptions } from "../SeatSelection";
import Frame from "../SeatSelection/Frame";
import type { HopperSeatMapOptions } from "../SeatSelection/types";

const getFontFamilies = (families: string[]) =>
  `font-family:${
    families?.length ? families.join(",") : ""
  } -apple-system, BlinkMacSystemFont, "Segoe UI",` +
  '  "Roboto", "Oxygen", "Ubuntu", "Cantarell", "Fira Sans", "Droid Sans",\n' +
  '  "Helvetica Neue", sans-serif;';

interface SeatsFrameComponentProps {
  styleString?: string;
  seatMapOptions?: SeatMapOptions;
  seatMapHtml?: string;
  segmentIndex?: number;
  isEditMode?: boolean;
  fontFamilies?: string[];
  fonts?: string[];
  onSetOpen: (val: boolean) => void;
  onCancel: () => void;
  onSeatsSelected: (
    selectedSeats: GordianSeat[],
    selectedSeatSegments: GordianSeatSegment[],
    selectedSeatTotal?: HopperSeatTotal
  ) => void;
  hopperStyleString?: string;
  hopperSeatMapOptions?: HopperSeatMapOptions;
}

const SeatsFrame = ({
  styleString,
  seatMapOptions,
  seatMapHtml,
  segmentIndex = 0,
  isEditMode = false,
  fontFamilies = [],
  fonts = [],
  onSetOpen,
  onCancel,
  onSeatsSelected,
  hopperStyleString,
  hopperSeatMapOptions,
}: SeatsFrameComponentProps) => {
  const { t } = useI18nContext();
  const [loading, setLoading] = useState(true);

  const prettySeatMap = useMemo(() => {
    return seatMapHtml.replace(
      "</head>",
      `<style>${
        fonts + getFontFamilies(fontFamilies) + styleString
      }</style></head>`
    );
  }, [fontFamilies, fonts, seatMapHtml, styleString]);

  const handleFrameLoaded = useCallback(() => {
    setLoading(false);
  }, []);

  return (
    <div className="SeatsGordian">
      <div className="SelectedSeats-header header">
        <BackButton
          className="mobile-flight-book-header-go-back"
          onClick={onCancel}
        />
        <h3>{t("seats.selectYourSeats")}</h3>
      </div>
      <B2BLoadingPopup
        open={loading}
        fullScreen
        message={t("seats.loadingMessage")}
        popupSize={"mobile"}
      />
      <Frame
        styleString={styleString}
        seatMapOptions={seatMapOptions}
        setSelectedSeats={onSeatsSelected}
        setOpen={onSetOpen}
        seatMapHtml={prettySeatMap}
        segmentIndex={segmentIndex}
        onEditMode={isEditMode}
        onFrameLoaded={handleFrameLoaded}
        hopperStringStyle={hopperStyleString}
        hopperSeatMapOptions={hopperSeatMapOptions}
      />
    </div>
  );
};

export default SeatsFrame;
